import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import { WebMercatorViewport } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer, PathLayer, PolygonLayer, ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import {
  Activity,
  ArrowLeft,
  CalendarClock,
  ClipboardCheck,
  Map as MapIcon,
  Play,
  Route,
  Signpost,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import "../styles/fornebu.css";

// Every figure on this page is from the forecast Telcofy made before the E18
// lane closure of 31 July to 3 August 2026 (RealTime forecast deck, slides 4–5).
// None of it is observed traffic.

// The forecast peak queue (4,675 vehicles, Saturday around 18:00) is pinned on the
// hero still, so the summary strip under the hero lists the other three measures.
const forecastMeasures = [
  { value: "165 min", label: "added delay", detail: "For a driver joining at the forecast peak" },
  { value: "53,590", label: "vehicle-hours of delay", detail: "Forecast across the closure" },
  { value: "8,970", label: "vehicles", detail: "Forecast to divert via Ring 3 and local roads" },
];

// The six times the forecast reports, from the lane closing to the target reopening.
const queueForecast = [
  { day: "Fri", dayLong: "Friday", time: "21:00", queue: 0, stage: "Lane closes" },
  { day: "Sat", dayLong: "Saturday", time: "14:00", queue: 2975, stage: "Building" },
  { day: "Sat", dayLong: "Saturday", time: "18:00", queue: 4675, stage: "Peak" },
  { day: "Sat", dayLong: "Saturday", time: "22:00", queue: 2805, stage: "Draining" },
  { day: "Sun", dayLong: "Sunday", time: "10:00", queue: 595, stage: "Rebuilding" },
  { day: "Sun", dayLong: "Sunday", time: "14:00", queue: 2280, stage: "Target reopening, still queued" },
];

// The closure plan. Hours from the lane closing set the spacing on the strip under
// the hero: 41 hours to the end of the lane reduction, then 16 more to the ramps.
const closurePlan = [
  { dateTime: "2026-07-31T21:00", when: "Fri 21:00", text: "E18 reduced to one Oslo-bound lane" },
  { dateTime: "2026-08-02T14:00", when: "Sun 14:00", text: "Lane reduction scheduled to end" },
  { dateTime: "2026-08-03T06:00", when: "Mon 06:00", text: "Fornebu, Lysaker and Ring 3 ramps reopen" },
];

const formatCount = value => value.toLocaleString("en-GB");

// Width and height of an element: measured before first paint, then kept up to
// date as the layout changes.
const useElementSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => {
      const width = Math.round(element.clientWidth);
      const height = Math.round(element.clientHeight);
      setSize(previous => (previous && previous.width === width && previous.height === height ? previous : { width, height }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
};

// ---- Forecast queue chart ---------------------------------------------------

const CHART_HEIGHT = 300;
const CHART_MAX = 5000;
const CHART_TICKS = [0, 1000, 2000, 3000, 4000, 5000];
const CHART_MARGIN = { top: 34, right: 8, bottom: 58, left: 48 };

const ForecastChart = () => {
  const [ref, size] = useElementSize();
  const [active, setActive] = useState(null);
  const width = size ? size.width : 720;
  const plotWidth = width - CHART_MARGIN.left - CHART_MARGIN.right;
  const plotBottom = CHART_HEIGHT - CHART_MARGIN.bottom;
  const plotHeight = plotBottom - CHART_MARGIN.top;
  const band = plotWidth / queueForecast.length;
  const barWidth = Math.min(28, band * 0.46);
  const cx = index => CHART_MARGIN.left + band * (index + 0.5);
  const cy = value => plotBottom - (value / CHART_MAX) * plotHeight;

  const barPath = (x, value) => {
    const top = cy(value);
    const half = barWidth / 2;
    const r = Math.min(4, plotBottom - top);
    return `M${x - half},${plotBottom} V${top + r} Q${x - half},${top} ${x - half + r},${top} H${x + half - r} Q${x + half},${top} ${x + half},${top + r} V${plotBottom} Z`;
  };

  // Day groups under the time labels: Friday (1 column), Saturday (3), Sunday (2).
  const dayGroups = queueForecast.reduce((groups, point, index) => {
    const last = groups[groups.length - 1];
    if (last && last.day === point.dayLong) last.end = index;
    else groups.push({ day: point.dayLong, start: index, end: index });
    return groups;
  }, []);

  const activePoint = active === null ? null : queueForecast[active];
  const labelled = new Set([2, queueForecast.length - 1]);

  return (
    <div className="fornebu-chart-plot" ref={ref}>
      <svg
        width="100%"
        height={CHART_HEIGHT}
        viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
        role="group"
        aria-labelledby="fornebu-chart-title"
      >
        {CHART_TICKS.map(tick => (
          <g key={tick}>
            <line className="gridline" x1={CHART_MARGIN.left} x2={width - CHART_MARGIN.right} y1={cy(tick)} y2={cy(tick)} />
            <text className="tick" x={CHART_MARGIN.left - 8} y={cy(tick)} dy="0.32em" textAnchor="end">{formatCount(tick)}</text>
          </g>
        ))}
        <line className="axis" x1={CHART_MARGIN.left} x2={width - CHART_MARGIN.right} y1={plotBottom} y2={plotBottom} />

        {queueForecast.map((point, index) => {
          const x = cx(index);
          const isActive = active === index;
          return (
            <g
              key={`${point.day}-${point.time}`}
              className={`bar-group${isActive ? " is-active" : ""}`}
              role="img"
              tabIndex={0}
              aria-label={`${point.dayLong} ${point.time}: ${formatCount(point.queue)} vehicles forecast in the queue. ${point.stage}.`}
              onMouseEnter={() => setActive(index)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            >
              <rect className="hit" x={x - band / 2} y={CHART_MARGIN.top - 20} width={band} height={plotHeight + 20} />
              {point.queue > 0
                ? <path className="bar" d={barPath(x, point.queue)} />
                : <circle className="bar zero" cx={x} cy={plotBottom} r={5} />}
              {labelled.has(index) && (
                <text className="value" x={x} y={cy(point.queue) - 9} textAnchor="middle">{formatCount(point.queue)}</text>
              )}
              <text className="time" x={x} y={plotBottom + 19} textAnchor="middle">{point.time}</text>
            </g>
          );
        })}

        {dayGroups.map(group => {
          const x1 = cx(group.start) - band / 2 + 5;
          const x2 = cx(group.end) + band / 2 - 5;
          return (
            <g key={group.day} className="day">
              <line x1={x1} x2={x2} y1={plotBottom + 31} y2={plotBottom + 31} />
              <text x={(x1 + x2) / 2} y={plotBottom + 48} textAnchor="middle">{group.day}</text>
            </g>
          );
        })}
      </svg>

      {activePoint && (
        <div
          className="fornebu-chart-tip"
          style={{ left: Math.min(Math.max(cx(active), 80), width - 80), top: cy(activePoint.queue) - 14 }}
          aria-hidden="true"
        >
          <strong>{formatCount(activePoint.queue)} vehicles</strong>
          <span>{activePoint.dayLong} {activePoint.time} · {activePoint.stage}</span>
        </div>
      )}
    </div>
  );
};

// ---- Illustrative monitoring map --------------------------------------------

// E18 centreline from Sandvika to Vækerø, simplified from OpenStreetMap.
const e18Road = [{
  path: [
    [10.5203, 59.8858], [10.5279, 59.8899], [10.5314, 59.891], [10.5481, 59.8945],
    [10.5556, 59.8953], [10.5989, 59.9033], [10.6123, 59.9066], [10.6237, 59.9088],
    [10.6357, 59.9125], [10.6402, 59.9132], [10.6596, 59.9173],
  ],
}];

// Illustrative zone shapes: buffers around the roads each zone is meant to cover.
const zoneShapes = [
  {
    number: "2",
    polygon: [
      [10.635, 59.9135], [10.6566, 59.9179], [10.6584, 59.9179], [10.6596, 59.9173], [10.6592, 59.916],
      [10.6572, 59.9155], [10.6408, 59.912], [10.6362, 59.9115], [10.6347, 59.9119], [10.6342, 59.9128],
      [10.635, 59.9135],
    ],
  },
  {
    number: "3",
    polygon: [
      [10.6253, 59.9109], [10.6273, 59.9149], [10.6266, 59.9167], [10.6269, 59.9179], [10.628, 59.9189],
      [10.6304, 59.92], [10.6315, 59.9222], [10.6331, 59.9236], [10.6362, 59.9249], [10.6396, 59.9257],
      [10.6496, 59.9272], [10.6514, 59.9269], [10.6523, 59.9261], [10.6518, 59.9251], [10.6511, 59.9248],
      [10.6414, 59.9234], [10.6383, 59.9226], [10.6366, 59.9216], [10.6348, 59.9187], [10.6316, 59.9171],
      [10.6323, 59.9145], [10.6298, 59.9104], [10.6282, 59.9098], [10.6263, 59.9101], [10.6253, 59.9109],
    ],
  },
  {
    number: "4",
    polygon: [
      [10.533, 59.8921], [10.5393, 59.8934], [10.5456, 59.8949], [10.5519, 59.8958], [10.5581, 59.8968],
      [10.5644, 59.8982], [10.5707, 59.8994], [10.577, 59.9005], [10.5833, 59.9015], [10.5896, 59.9027],
      [10.5959, 59.904], [10.6021, 59.9051], [10.6084, 59.9064], [10.6147, 59.908], [10.621, 59.9089],
      [10.621, 59.9095], [10.6147, 59.911], [10.6084, 59.9112], [10.6021, 59.9113], [10.5959, 59.9113],
      [10.5896, 59.9108], [10.5833, 59.9101], [10.577, 59.9093], [10.5707, 59.908], [10.5644, 59.9063],
      [10.5581, 59.9041], [10.5519, 59.902], [10.5456, 59.8997], [10.5393, 59.8965], [10.533, 59.8927],
      [10.533, 59.8921],
    ],
  },
];

const monitoringAreas = [
  {
    number: "1",
    badge: [10.6283, 59.9097],
    role: "Bottleneck",
    title: "Fornebukrysset",
    description: "Where the three Oslo-bound lanes taper to one. The tightest point of the weekend.",
  },
  {
    number: "2",
    badge: [10.6485, 59.9149],
    role: "Queue corridor",
    title: "E18, Lysaker to Vækerø",
    description: "The Oslo-bound E18 east of the lane drop, which carries traffic on the signed detour via Vækerø.",
  },
  {
    number: "3",
    badge: [10.6405, 59.9245],
    role: "Diversion route",
    title: "Ring 3 through Granfosstunnelen",
    description: "The signed alternative into Oslo. On an average day it carries about 29,000 vehicles.",
  },
  {
    number: "4",
    badge: [10.578, 59.905],
    role: "Spillover",
    title: "Local roads in Asker and Bærum",
    description: "Where drivers are expected to cut through once the E18 queue grows long.",
  },
];

const MAP_BOUNDS = [[10.521, 59.8855], [10.662, 59.9295]];
const MAP_FALLBACK_VIEW = { longitude: 10.592, latitude: 59.907, zoom: 11.3, pitch: 0, bearing: 0 };

const FornebuMap = () => {
  const [ref, size] = useElementSize();
  const viewState = useMemo(() => {
    if (!size || size.width < 200 || size.height < 200) return MAP_FALLBACK_VIEW;
    const { longitude, latitude, zoom } = new WebMercatorViewport(size).fitBounds(MAP_BOUNDS, {
      padding: { top: 80, bottom: 52, left: 16, right: 16 },
    });
    return { longitude, latitude, zoom, pitch: 0, bearing: 0 };
  }, [size]);

  return (
    <figure
      className="fornebu-map"
      ref={ref}
      aria-label="Illustrative map of the four monitoring zones for the E18 lane closure: 1 Fornebukrysset, 2 the E18 from Lysaker to Vækerø, 3 Ring 3 through Granfosstunnelen, 4 local roads in Asker and Bærum north of the E18."
    >
      <DeckGL
        viewState={viewState}
        controller={false}
        layers={[
          new TileLayer({
            id: "fornebu-osm",
            data: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
            minZoom: 0,
            maxZoom: 19,
            tileSize: 256,
            renderSubLayers: props => {
              const { west, south, east, north } = props.tile.bbox;
              return new BitmapLayer({ id: props.id, image: props.data, bounds: [west, south, east, north] });
            },
          }),
          new PathLayer({
            id: "fornebu-e18-casing",
            data: e18Road,
            getPath: d => d.path,
            getColor: [255, 255, 255, 235],
            getWidth: 10,
            widthUnits: "pixels",
            capRounded: true,
            jointRounded: true,
          }),
          new PathLayer({
            id: "fornebu-e18",
            data: e18Road,
            getPath: d => d.path,
            getColor: [44, 58, 51, 235],
            getWidth: 5,
            widthUnits: "pixels",
            capRounded: true,
            jointRounded: true,
          }),
          new PolygonLayer({
            id: "fornebu-zone-shapes",
            data: zoneShapes,
            getPolygon: d => d.polygon,
            getFillColor: [76, 175, 80, 80],
            getLineColor: [46, 125, 50, 240],
            lineWidthUnits: "pixels",
            getLineWidth: 2,
            stroked: true,
            filled: true,
          }),
          new ScatterplotLayer({
            id: "fornebu-zone-bottleneck",
            data: [monitoringAreas[0]],
            getPosition: d => d.badge,
            getRadius: 330,
            radiusUnits: "meters",
            getFillColor: [76, 175, 80, 80],
            getLineColor: [46, 125, 50, 240],
            lineWidthMinPixels: 2,
            stroked: true,
          }),
          new ScatterplotLayer({
            id: "fornebu-zone-badges",
            data: monitoringAreas,
            getPosition: d => d.badge,
            getRadius: 11,
            radiusUnits: "pixels",
            getFillColor: [46, 125, 50, 255],
            getLineColor: [255, 255, 255, 255],
            lineWidthMinPixels: 2,
            stroked: true,
          }),
          new TextLayer({
            id: "fornebu-zone-numbers",
            data: monitoringAreas,
            getPosition: d => d.badge,
            getText: d => d.number,
            getColor: [255, 255, 255, 255],
            getSize: 12,
            fontFamily: "Inter, system-ui, sans-serif",
            fontWeight: 700,
            getTextAnchor: "middle",
            getAlignmentBaseline: "center",
          }),
        ]}
      />
      <div className="fornebu-map-key" aria-hidden="true">
        <span><i className="is-road" />E18</span>
        <span><i className="is-zone" />Monitoring zone (illustrative)</span>
      </div>
      <figcaption>Basemap © OpenStreetMap contributors. The zone shapes are illustrative and do not show exact monitoring boundaries.</figcaption>
    </figure>
  );
};

// ---- Page -------------------------------------------------------------------

const FornebuPage = () => (
  <div className="fornebu-page">
    <Navbar />
    <main className="pt-16">
      <header className="fornebu-hero">
        <div className="fornebu-hero-inner">
          <div className="fornebu-hero-copy">
            <Link to="/projects" className="fornebu-back"><ArrowLeft size={15} aria-hidden="true" /> Projects</Link>
            <p className="fornebu-kicker">Road impact forecast · E18 Fornebukrysset</p>
            <h1>Planning for an E18 lane closure</h1>
            <p className="fornebu-deck">
              Before a planned lane closure on the E18 at Fornebukrysset, we forecast how long the Oslo-bound queue would get,
              how much delay it would add and how many drivers would divert. We also defined four zones where live network
              data could be compared with the forecast during the works.
            </p>
            <a className="fornebu-watch" href="#fornebu-animation">
              <span aria-hidden="true"><Play size={13} fill="currentColor" /></span>
              Watch the forecast animation
            </a>
            <div className="fornebu-meta">
              <span><CalendarClock size={15} aria-hidden="true" />31 July to 3 August 2026</span>
              <span><MapIcon size={15} aria-hidden="true" />Fornebu and Lysaker</span>
              <span><Route size={15} aria-hidden="true" />Oslo-bound E18</span>
            </div>
          </div>
        </div>

        {/* The still is drawn larger than the hero and cropped to the map, clear of the
            figures and inset baked into the frame. The pin sits in the frame's own
            coordinates, on the line the animation draws from Fornebukrysset. */}
        <figure className="fornebu-hero-visual">
          <div className="fornebu-hero-stage">
            <img
              src="/fornebu/forecast-still.jpg"
              width="1600"
              height="900"
              decoding="async"
              alt="Frame from the forecast animation at Saturday 18:00, on a dark map of the E18 from Sandvika to Oslo. A red line marks the forecast tailback, running back from Fornebukrysset towards Sandvika."
            />
            <div className="fornebu-hero-pin">
              <p className="fornebu-hero-pin-label">Forecast peak queue</p>
              <p className="fornebu-hero-pin-figure"><strong>4,675</strong> vehicles</p>
              <p className="fornebu-hero-pin-when">Saturday around 18:00, at Fornebukrysset</p>
              <p className="fornebu-hero-pin-key"><i aria-hidden="true" /><span className="sr-only">Red line: </span>Forecast tailback</p>
            </div>
          </div>
          <div className="fornebu-hero-shade" aria-hidden="true" />
          <figcaption className="fornebu-hero-credit">
            Still from the forecast animation. Map data ©{" "}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors.
          </figcaption>
        </figure>
      </header>

      <section className="fornebu-plan" aria-labelledby="fornebu-plan-title">
        <div className="fornebu-plan-head">
          <p id="fornebu-plan-title">Closure plan</p>
          <span>Signed detour via Vækerø</span>
        </div>
        <ol>
          {closurePlan.map(({ dateTime, when, text }) => (
            <li key={dateTime}><time dateTime={dateTime}>{when}</time><span>{text}</span></li>
          ))}
        </ol>
      </section>

      <section className="fornebu-forecast" aria-labelledby="fornebu-forecast-title">
        <div className="fornebu-forecast-head">
          <p id="fornebu-forecast-title">Forecast summary</p>
          <span>Made before the closure of 31 July to 3 August 2026</span>
        </div>
        <div className="fornebu-measures">
          {forecastMeasures.map(({ value, label, detail }) => (
            <article key={detail}><strong>{value}</strong><small>{label}</small><p>{detail}</p></article>
          ))}
        </div>
      </section>

      <section className="fornebu-intro fornebu-wrap">
        <h2>One Oslo-bound lane for 41 hours</h2>
        <div className="fornebu-intro-text">
          <p>
            To lift the first sections of a new cycle bridge into place at Fornebukrysset, the plan cut the Oslo-bound E18
            from three lanes to one from Friday 21:00 to Sunday 14:00. The E18 ramps to Fornebu, Lysaker and Ring 3 at
            Granfosstunnelen were to stay closed until Monday 06:00, with a signed detour via Vækerø.
          </p>
          <p>
            The weekend came at the tail end of fellesferie, the main Norwegian summer holiday period, when traffic returning
            from holiday cabins peaks. The road authority had warned of extra-long queues into Oslo that weekend.
          </p>
        </div>
      </section>

      <section className="fornebu-timeline">
        <div className="fornebu-wrap">
          <div className="fornebu-timeline-head">
            <h2>The queue was forecast to peak on Saturday evening</h2>
            <p>
              In the forecast, Friday's getaway traffic has already eased to about the reduced capacity by the time the lane
              closes at 21:00, so the queue starts from zero. It builds through Saturday to 4,675 vehicles around 18:00, then
              drains overnight. Sunday's cabin-return traffic rebuilds it: about 2,280 vehicles are still forecast to be
              queuing at the 14:00 target reopening, so traffic may take a few more hours to return to normal.
            </p>
          </div>

          <figure className="fornebu-chart">
            <figcaption>
              <strong id="fornebu-chart-title">Forecast queue at Fornebukrysset, in vehicles</strong>
              <span>At the six times reported in the forecast, from the lane closing to the target reopening. Forecast made before the closure.</span>
            </figcaption>
            <ForecastChart />
            <details className="fornebu-table">
              <summary>Show the forecast as a table</summary>
              <table>
                <thead>
                  <tr><th scope="col">Time</th><th scope="col">Forecast queue (vehicles)</th><th scope="col">Stage</th></tr>
                </thead>
                <tbody>
                  {queueForecast.map(point => (
                    <tr key={`${point.day}-${point.time}`}>
                      <th scope="row">{point.dayLong} {point.time}</th>
                      <td>{formatCount(point.queue)}</td>
                      <td>{point.stage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </figure>
        </div>
      </section>

      <section className="fornebu-video" id="fornebu-animation">
        <div className="fornebu-wrap">
          <div className="fornebu-video-head">
            <h2>The forecast as an animation</h2>
            <p>
              A 52-second animation of the forecast. It follows the queue from Friday 21:00 to the Saturday peak, shows the
              local roads expected to take diverted traffic and marks the four monitoring zones. Its queue and delay figures
              are forecasts.
            </p>
          </div>
          <div className="fornebu-video-frame">
            <iframe
              src="https://player.vimeo.com/video/1217593179?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&dnt=1"
              title="E18 lane closure forecast animation"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="fornebu-monitoring">
        <div className="fornebu-wrap fornebu-monitoring-grid">
          <div className="fornebu-monitoring-copy">
            <h2>Four zones to watch during the closure</h2>
            <p>
              The zones cover the lane drop, the E18 from Lysaker to Vækerø, the signed alternative route on Ring 3 and the
              local roads drivers may use to avoid the queue. The shapes on the map are illustrative.
            </p>
            <div className="fornebu-area-list">
              {monitoringAreas.map(({ number, role, title, description }) => (
                <div key={number}>
                  <b>{number}</b>
                  <span><em>{role}</em><strong>{title}</strong><small>{description}</small></span>
                </div>
              ))}
            </div>
          </div>
          <FornebuMap />
        </div>
      </section>

      <section className="fornebu-operations fornebu-wrap">
        <div className="fornebu-operations-head">
          <h2>How the zones would be used during a closure</h2>
          <p>
            Each zone would report aggregated counts of people present, so the forecast could be checked against live
            figures while the lane is closed.
          </p>
        </div>
        <div className="fornebu-operation-cards">
          <article><Activity size={23} aria-hidden="true" /><h3>Queue growth</h3><p>Flag the queue as soon as it grows faster than forecast, while it is still forming.</p></article>
          <article><Signpost size={23} aria-hidden="true" /><h3>Detour signs</h3><p>Switch variable message signs towards the Vækerø detour when the zone counts show the queue building.</p></article>
          <article><Route size={23} aria-hidden="true" /><h3>Ring 3 and local roads</h3><p>Watch Ring 3 through Granfosstunnelen and the local roads in Asker and Bærum for drivers cutting through.</p></article>
          <article><ClipboardCheck size={23} aria-hidden="true" /><h3>Forecast check</h3><p>Compare the forecast with what happened and adjust the model before the next closure weekend.</p></article>
        </div>
      </section>

      <section className="fornebu-data">
        <h2>Where the zone counts come from</h2>
        <p>
          The figures come from a mobile network operator's live data: aggregated, anonymised counts of people in each
          zone. Nothing is reported about individual devices or vehicles. The method is built to combine data from more than one
          operator.
        </p>
      </section>

      <aside className="fornebu-note" aria-labelledby="fornebu-note-title">
        <strong id="fornebu-note-title">About the figures</strong>
        <p>
          The queue, delay and diversion figures on this page are forecasts made before the closure. No traffic observed
          during the closure weekend is shown. We built the forecast from the published annual average daily traffic on
          the E18 at Lysaker (about 90,000 vehicles) and the planned lane reduction, which cuts Oslo-bound capacity from
          about 5,700 to about 1,700 vehicles an hour. A cumulative queuing model compares hourly demand, scaled for
          Friday departures and Sunday cabin returns, with that capacity. Once congestion builds, 15% of demand during the
          restricted hours is modelled as diverting. With all three lanes open, the model shows no queue. The Ring 3 figure
          of about 29,000 vehicles a day is also a published annual average. The forecast uses no mobile network data.
        </p>
        <p>Traffic volumes: Statens vegvesen (NLOD). The monitoring zones on the map are illustrative.</p>
      </aside>

      <CaseStudyCta
        question="Planning a lane closure and need to know how long the queue will get?"
        body="We can forecast queue length, delay and diversion before the works start, and set up zones that compare live counts with the forecast while the road is restricted."
        extra={
          <Link to="/platform/privacy" className="font-semibold text-brand-700 underline underline-offset-4 hover:text-brand-600">
            How we process mobile-network data
          </Link>
        }
      />
    </main>
  </div>
);

export default FornebuPage;
